import React, { useState, useEffect, useRef } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { Filter, X } from 'lucide-react';
import type { Column, Table } from '@tanstack/react-table';
import {
  FilterStyle,
  DataTableColumn,
} from '../types';
import ColumnFilterPanel, {
  ColumnFilterPanelHandle,
  buildFilterValue,
  computeDistinctValues,
} from './ColumnFilterPanel';

interface ColumnFilterProps<TData> {
  column: Column<TData>;
  table: Table<TData>;
  filterStyle?: FilterStyle;
}

export default function ColumnFilter<TData>({ column, table, filterStyle = 'select' }: ColumnFilterProps<TData>) {
  const dialog = useDialogStore();
  const panelRef = useRef<ColumnFilterPanelHandle>(null);
  const [panelKey, setPanelKey] = useState(0);

  // Subscribe to the dialog's open state reactively so the useEffect
  // below reliably detects open/close transitions.
  const isOpen = dialog.useState('open');

  const columnDef = column.columnDef as DataTableColumn<unknown>;
  const hasAdvancedFilters = !!columnDef.columnFilterType;

  // Re-mount panel each time dialog opens so it re-reads column state
  useEffect(() => {
    if (isOpen) {
      setPanelKey(prev => prev + 1);
    }
  }, [isOpen]);

  const handleApply = () => {
    const state = panelRef.current?.getState();
    if (!state) {
      dialog.hide();
      return;
    }

    const distinctValues = computeDistinctValues(column, table, filterStyle);
    const filterValue = buildFilterValue(
      state.selectedValues,
      state.advancedRules,
      distinctValues,
      hasAdvancedFilters,
      filterStyle,
      state.searchValue,
    );

    column.setFilterValue(filterValue);
    dialog.hide();
  };

  const handleClear = () => {
    column.setFilterValue(undefined);
    dialog.hide();
  };

  const isFiltered = column.getIsFiltered();

  return (
    <>
      <Button
        onClick={dialog.toggle}
        className={`p-1 rounded hover:bg-gray-100 ${isFiltered ? 'text-blue-600' : 'text-gray-400'}`}
        aria-label={`Filter ${column.id}`}
        data-testid={`column-filter-button-${column.id}`}
        data-filtered={isFiltered ? "true" : undefined}
        title="Click to filter"
      >
        <Filter className="h-4 w-4" />
      </Button>

      <Dialog
        store={dialog}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        backdrop={<div className="fixed inset-0 bg-black/30" />}
        data-testid="filter-dialog"
      >
        <div className="bg-white rounded-lg shadow-xl w-96 h-[600px] flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-semibold">Filter: {column.id}</h2>
            <Button
              onClick={dialog.hide}
              className="p-1 rounded hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <ColumnFilterPanel
              key={panelKey}
              ref={panelRef}
              column={column}
              table={table}
              filterStyle={filterStyle}
              autoFocusSearch
            />
          </div>

          <div className="px-4 py-3 border-t">
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleClear}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Clear Filter
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
